/*!
 * BelmourdeStoryBg — "Placa grabada"
 * Fondo animado del bloque "Quiénes somos" de Belmourde Barbería:
 * el logo de la casa en blanco y negro, grande y centrado detrás del texto,
 * tratado como una proyección de vídeo antiguo (respiración lenta, barrido de
 * luz que lo hace brillar, grano de película a 12 fps, parpadeo y leve
 * temblor de proyector, viñeta por pantalla y alguna raya de celuloide).
 * Todo monocromo; el oro vive en el texto.
 *
 * Canvas 2D puro, sin dependencias, sintaxis ES5. El canvas mide el ancho de
 * la sección por el alto de la ventana y se queda pegado (sticky) mientras la
 * sección está en pantalla: se compone por pantalla y nunca supera el área de
 * canvas que tolera iOS Safari. Escritorio a 1x y 30 fps: la animación es
 * lenta y no necesita más.
 *
 * API:
 *   window.BelmourdeStoryBg.init(sectionEl, opts)
 *   window.BelmourdeStoryBg.destroy()
 *
 * opts (opcionales): logoSrc (por defecto data-logo de la sección o el logo
 *   de la cabecera), mobileBreakpoint (768), debugTime (segundos fijos, solo
 *   para capturas).
 */
(function (global) {
  'use strict';

  function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }
  function makeRand(seed) {
    var s = seed || 1;
    return function () { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; };
  }
  // Hash determinista por paso: parpadeo y temblor se repiten igual en cada visita.
  function hashStep(n) { var x = Math.sin(n * 12.9898) * 43758.5453; return x - Math.floor(x); }

  var DESKTOP = {
    alphaMean: 0.16, alphaAmp: 0.025, breathePeriod: 9.5,
    // La columna de contenido ocupa el 12-88 % del ancho a 1440: ahí el logo
    // baja a 0.75 para que no compita con el texto; en los márgenes va entero.
    maskCore: [0.12, 0.88], maskInner: 0.75,
    sweepCore: 220, sweepFeather: 300, sweepAngle: 22, sweepPeak: 0.10, sweepCycle: 22, sweepTravel: 16,
    grainAlpha: 0.14, vignette: 0.28, anchorY: 0.48, scratchAlpha: 0.12, jitter: 0.6,
    maxDpr: 1
  };
  var MOBILE = {
    alphaMean: 0.15, alphaAmp: 0.025, breathePeriod: 9.5,
    maskCore: [0.10, 0.90], maskInner: 0.7,
    sweepCore: 140, sweepFeather: 220, sweepAngle: 18, sweepPeak: 0.08, sweepCycle: 20, sweepTravel: 14,
    grainAlpha: 0.12, vignette: 0.30, anchorY: 0.42, scratchAlpha: 0.10, jitter: 0.5,
    maxDpr: 1.5
  };
  var GRAIN_TILE = 192;      // px CSS (el patrón se pinta en espacio CSS: grano de 1 px CSS, visible en retina)
  var GRAIN_FPS = 12;        // cambio de tile cada ~83 ms
  var FLICKER_STEP = 0.14;   // s
  var MIN_FRAME_MS = 31;     // ~30 fps
  var BASE = '#050505';
  // Recorte del dibujo dentro del PNG del logo si no se puede medir (file://).
  var ART_FALLBACK = { x: 0.033, y: 0.393, w: 0.927, h: 0.214 };

  function BelmourdeStoryBg() {
    this._destroyed = true;
    this._reduced = false;
    this._visible = true;
    this._raf = null;
    this._cleanupInternal = null;
  }

  BelmourdeStoryBg.prototype.init = function (sectionEl, opts) {
    if (!sectionEl) return;
    this.destroy();
    opts = opts || {};
    var self = this;
    var headerLogo = document.querySelector('.header__heading-logo');
    var logoSrc = opts.logoSrc || sectionEl.getAttribute('data-logo') ||
      (headerLogo && headerLogo.getAttribute('src')) || 'images/belmourde-barberia-logo-blanco.png';
    var mobileBreakpoint = opts.mobileBreakpoint || 768;
    var debugTime = (typeof opts.debugTime === 'number') ? opts.debugTime : null;

    var reduced = false;
    try { reduced = !!(global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) {}
    this._reduced = reduced;

    var prevPosition = sectionEl.style.position, prevOverflow = sectionEl.style.overflow;
    if (global.getComputedStyle(sectionEl).position === 'static') sectionEl.style.position = 'relative';

    // position:sticky no funciona bajo un ancestro con overflow:hidden; la
    // sección lo usa para recortar las animaciones de entrada, así que se pasa
    // a overflow:clip (mismo recorte, sticky intacto). Sin soporte: translateY.
    var usingSticky = false;
    try {
      if (global.CSS && global.CSS.supports && global.CSS.supports('overflow', 'clip')) {
        sectionEl.style.overflow = 'clip';
        usingSticky = true;
      }
    } catch (e) {}

    // ---------------------------------------------------------------- DOM
    var root = document.createElement('div');
    root.className = 'belmourde-story-bg';
    root.setAttribute('aria-hidden', 'true');
    root.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;z-index:0;overflow:visible;pointer-events:none;';

    var base = document.createElement('div');
    base.style.cssText = 'position:absolute;inset:0;background:' + BASE + ';z-index:0;';

    var stickyWrap = document.createElement('div');
    stickyWrap.className = 'bsb-stickywrap';
    stickyWrap.style.cssText = 'position:' + (usingSticky ? 'sticky' : 'absolute') + ';top:0;left:0;width:100%;z-index:1;overflow:hidden;';

    var canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block;';
    var ctx = canvas.getContext('2d', { alpha: false });
    stickyWrap.appendChild(canvas);

    // Viñeta POR PANTALLA (dentro del sticky): oscurece las esquinas de cada
    // vista, no solo los laterales de una sección de miles de píxeles.
    var vignette = document.createElement('div');
    vignette.className = 'bsb-vignette';
    vignette.style.cssText = 'position:absolute;inset:0;';
    stickyWrap.appendChild(vignette);

    // Fundidos a negro arriba y abajo de la sección: sin corte duro entre el
    // canvas (gris muy oscuro con grano) y el negro de la página.
    var fadeTop = document.createElement('div');
    fadeTop.style.cssText = 'position:absolute;left:0;right:0;top:0;height:140px;z-index:3;background:linear-gradient(to bottom,#000,rgba(0,0,0,0));';
    var fadeBottom = document.createElement('div');
    fadeBottom.style.cssText = 'position:absolute;left:0;right:0;bottom:0;height:220px;z-index:3;background:linear-gradient(to top,#000,rgba(0,0,0,0));';

    root.appendChild(base);
    root.appendChild(stickyWrap);
    root.appendChild(fadeTop);
    root.appendChild(fadeBottom);
    sectionEl.appendChild(root);
    this._root = root;

    // ---------------------------------------------------------------- Estado
    var cfg = DESKTOP, mobile = false;
    var W = 0, H = 0, DPR = 1, sectionHeight = 0;
    var logoImg = new Image(), logoReady = false;
    var logoLayer = null;   // offscreen W×H (px dispositivo): logo escalado + máscara de zona segura
    var boostLayer = null;  // offscreen del tamaño del logo: logo × banda de luz
    var logoBox = { x: 0, y: 0, w: 0, h: 0 };
    var art = null;         // recorte real del dibujo dentro del PNG (franja apaisada en lienzo cuadrado)
    var grainPatterns = [], grainOffsets = [];
    var rand = makeRand(20260927);
    var t0 = performance.now();
    var lastFrame = 0;
    var flicker = 1, lastFlickerStep = -1;
    var nextScratchAt = 5 + rand() * 4, scratchUntil = -1, scratchX = 0;
    var scrolledPast = 0, progress = 0, scrollParallax = 0;

    function configure() {
      mobile = global.innerWidth < mobileBreakpoint;
      cfg = mobile ? MOBILE : DESKTOP;
      vignette.style.background = 'radial-gradient(ellipse at 50% 50%, rgba(0,0,0,0) 45%, rgba(0,0,0,' + cfg.vignette + ') 100%)';
    }

    // Caja de pintado del dibujo (no del PNG entero): casi todo el ancho de la
    // ventana, para que el logo se reconozca entero también en móvil.
    function layoutLogo() {
      var aspect = art ? art.h / art.w : ART_FALLBACK.h / ART_FALLBACK.w;
      var w = mobile ? 0.98 * W : 0.92 * W;
      var h = w * aspect;
      if (!mobile && h > 0.6 * H) { h = 0.6 * H; w = h / aspect; }
      logoBox.w = w; logoBox.h = h;
      logoBox.x = W / 2 - w / 2;
      logoBox.y = cfg.anchorY * H - h / 2;
    }

    // Busca el rectángulo con tinta del PNG en una miniatura (una sola vez).
    // Desde file:// getImageData lanza SecurityError: se usa el recorte conocido.
    function measureArt() {
      var nw = logoImg.naturalWidth, nh = logoImg.naturalHeight;
      art = { x: ART_FALLBACK.x * nw, y: ART_FALLBACK.y * nh, w: ART_FALLBACK.w * nw, h: ART_FALLBACK.h * nh };
      try {
        var n = 100, c = document.createElement('canvas');
        c.width = n; c.height = n;
        var g = c.getContext('2d');
        g.drawImage(logoImg, 0, 0, n, n);
        var d = g.getImageData(0, 0, n, n).data;
        var x0 = n, y0 = n, x1 = -1, y1 = -1;
        for (var y = 0; y < n; y++) for (var x = 0; x < n; x++) {
          if (d[(y * n + x) * 4 + 3] > 20) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
        }
        if (x1 < 0) return;
        var kx = nw / n, ky = nh / n;
        art = { x: Math.max(0, (x0 - 1) * kx), y: Math.max(0, (y0 - 1) * ky),
                w: Math.min(nw, (x1 - x0 + 3) * kx), h: Math.min(nh, (y1 - y0 + 3) * ky) };
      } catch (e) {}
    }

    // Logo ya escalado y con la máscara horizontal aplicada: bajo la columna de
    // contenido la alpha se reduce a maskInner, en los márgenes queda entera.
    function buildLogoLayer() {
      if (!logoReady) return;
      var cw = Math.round(W * DPR), ch = Math.round(H * DPR);
      if (!logoLayer) logoLayer = document.createElement('canvas');
      logoLayer.width = cw; logoLayer.height = ch;
      var lc = logoLayer.getContext('2d');
      lc.clearRect(0, 0, cw, ch);
      lc.drawImage(logoImg, art.x, art.y, art.w, art.h, logoBox.x * DPR, logoBox.y * DPR, logoBox.w * DPR, logoBox.h * DPR);
      var c0 = cfg.maskCore[0], c1 = cfg.maskCore[1], inner = cfg.maskInner;
      var g = lc.createLinearGradient(0, 0, cw, 0);
      g.addColorStop(0, 'rgba(0,0,0,1)');
      g.addColorStop(Math.max(0, c0 - 0.06), 'rgba(0,0,0,1)');
      g.addColorStop(c0, 'rgba(0,0,0,' + inner + ')');
      g.addColorStop(c1, 'rgba(0,0,0,' + inner + ')');
      g.addColorStop(Math.min(1, c1 + 0.06), 'rgba(0,0,0,1)');
      g.addColorStop(1, 'rgba(0,0,0,1)');
      lc.globalCompositeOperation = 'destination-in';
      lc.fillStyle = g;
      lc.fillRect(0, 0, cw, ch);
      lc.globalCompositeOperation = 'source-over';
    }

    // Cuatro tiles de ruido gris precalculados; por frame solo un fill con patrón.
    function buildGrain() {
      grainPatterns = []; grainOffsets = [];
      for (var t = 0; t < 4; t++) {
        var c = document.createElement('canvas');
        c.width = GRAIN_TILE; c.height = GRAIN_TILE;
        var g = c.getContext('2d');
        var id = g.createImageData(GRAIN_TILE, GRAIN_TILE), d = id.data;
        for (var i = 0; i < d.length; i += 4) {
          var v = Math.round(rand() * 90); // 0-90: en modo screen solo aclara, como el grano en las sombras
          d[i] = d[i + 1] = d[i + 2] = v; d[i + 3] = 255;
        }
        g.putImageData(id, 0, 0);
        grainPatterns.push(ctx.createPattern(c, 'repeat'));
        grainOffsets.push([Math.round(rand() * GRAIN_TILE), Math.round(rand() * GRAIN_TILE)]);
      }
    }

    function resize() {
      configure();
      var rect = sectionEl.getBoundingClientRect();
      sectionHeight = Math.max(1, Math.round(rect.height));
      W = Math.max(1, Math.round(rect.width));
      H = Math.max(1, Math.min(global.innerHeight, sectionHeight));
      stickyWrap.style.height = H + 'px';
      DPR = clamp(global.devicePixelRatio || 1, 1, cfg.maxDpr);
      canvas.width = Math.round(W * DPR);
      canvas.height = Math.round(H * DPR);
      canvas.style.width = W + 'px';
      canvas.style.height = H + 'px';
      layoutLogo();
      buildLogoLayer();
      if (!grainPatterns.length) buildGrain();
    }

    function updateScroll() {
      var rect = sectionEl.getBoundingClientRect();
      var maxOffset = Math.max(1, sectionHeight - H);
      scrolledPast = clamp(-rect.top, 0, maxOffset);
      progress = scrolledPast / maxOffset;
      scrollParallax = scrolledPast * 0.025;
      if (!usingSticky) stickyWrap.style.transform = 'translateY(' + scrolledPast.toFixed(1) + 'px)';
    }

    // ---------------------------------------------------------------- Dibujo
    function sweepX(timeSec) {
      var tc = timeSec % cfg.sweepCycle;
      if (tc > cfg.sweepTravel) return null; // pausa entre pasadas
      return -0.30 * W + (tc / cfg.sweepTravel) * 1.60 * W; // -30vw → 130vw
    }

    function bandGradient(c, half, feather, peak) {
      var g = c.createLinearGradient(-half, 0, half, 0);
      var f = feather / (2 * half);
      g.addColorStop(0, 'rgba(255,255,255,0)');
      g.addColorStop(f * 0.5, 'rgba(255,255,255,' + (peak * 0.25).toFixed(4) + ')');
      g.addColorStop(f, 'rgba(255,255,255,' + peak.toFixed(4) + ')');
      g.addColorStop(1 - f, 'rgba(255,255,255,' + peak.toFixed(4) + ')');
      g.addColorStop(1 - f * 0.5, 'rgba(255,255,255,' + (peak * 0.25).toFixed(4) + ')');
      g.addColorStop(1, 'rgba(255,255,255,0)');
      return g;
    }

    // Banda diagonal de luz en modo screen sobre todo el fotograma.
    function drawSweep(x, peak) {
      var half = cfg.sweepCore / 2 + cfg.sweepFeather;
      var D = Math.max(W, H) * 2;
      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      ctx.translate(x, H / 2);
      ctx.rotate(cfg.sweepAngle * Math.PI / 180);
      ctx.fillStyle = bandGradient(ctx, half, cfg.sweepFeather, peak);
      ctx.fillRect(-half, -D, half * 2, D * 2);
      ctx.restore();
    }

    // El logo "brilla" al paso de la luz: copia del logo recortada a su caja,
    // multiplicada por la banda y sumada encima con alpha extra.
    function drawLogoBoost(x, extraAlpha) {
      var bw = Math.round(logoBox.w * DPR), bh = Math.round(logoBox.h * DPR);
      if (bw < 1 || bh < 1) return;
      if (!boostLayer) boostLayer = document.createElement('canvas');
      if (boostLayer.width !== bw || boostLayer.height !== bh) { boostLayer.width = bw; boostLayer.height = bh; }
      var bc = boostLayer.getContext('2d');
      bc.setTransform(1, 0, 0, 1, 0, 0);
      bc.globalCompositeOperation = 'source-over';
      bc.clearRect(0, 0, bw, bh);
      bc.drawImage(logoLayer, Math.round(logoBox.x * DPR), Math.round(logoBox.y * DPR), bw, bh, 0, 0, bw, bh);
      bc.globalCompositeOperation = 'destination-in';
      bc.translate((x - logoBox.x) * DPR, (H / 2 - logoBox.y) * DPR);
      bc.rotate(cfg.sweepAngle * Math.PI / 180);
      var half = (cfg.sweepCore / 2 + cfg.sweepFeather) * DPR;
      var D = Math.max(bw, bh) * 2;
      bc.fillStyle = bandGradient(bc, half, cfg.sweepFeather * DPR, 1);
      bc.fillRect(-half, -D, half * 2, D * 2);
      bc.setTransform(1, 0, 0, 1, 0, 0);
      bc.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = extraAlpha;
      ctx.drawImage(boostLayer, 0, 0, bw, bh, logoBox.x, logoBox.y, logoBox.w, logoBox.h);
    }

    // Grano en espacio CSS (1 px CSS por grano): se ve también en retina.
    function drawGrain(idx, alpha) {
      if (!grainPatterns.length) return;
      var off = grainOffsets[idx];
      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      ctx.globalAlpha = alpha;
      ctx.translate(off[0], off[1]);
      ctx.fillStyle = grainPatterns[idx];
      ctx.fillRect(-off[0], -off[1], W + GRAIN_TILE, H + GRAIN_TILE);
      ctx.restore();
    }

    function drawScratch(x, alpha) {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = '#fff';
      ctx.fillRect(Math.round(x), 0, 1.5, H);
      ctx.restore();
    }

    function render(timeSec, isStatic) {
      timeSec = Math.max(0, timeSec || 0);
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = BASE;
      ctx.fillRect(0, 0, W, H);

      var fl = 1, breathe = 0, sx = null, sweepPeak = cfg.sweepPeak, grainIdx = 0, parallax = 0, prog = 0.5, jx = 0, jy = 0;
      if (!isStatic) {
        var step = Math.floor(timeSec / FLICKER_STEP);
        if (step !== lastFlickerStep) { lastFlickerStep = step; flicker = 0.93 + hashStep(step) * 0.07; }
        fl = flicker;
        breathe = Math.sin((timeSec / cfg.breathePeriod) * Math.PI * 2);
        sx = sweepX(timeSec);
        var frame = Math.floor(timeSec * GRAIN_FPS);
        grainIdx = ((frame % 4) + 4) % 4;
        // Temblor de proyector: medio píxel a 12 fps, determinista.
        jx = (hashStep(frame * 3 + 1) - 0.5) * 2 * cfg.jitter;
        jy = (hashStep(frame * 3 + 2) - 0.5) * 2 * cfg.jitter;
        parallax = scrollParallax;
        prog = progress;
      } else {
        // Fotograma único: barrido congelado al 42 % del ancho a media intensidad.
        sx = 0.42 * W;
        sweepPeak = cfg.sweepPeak * 0.5;
      }

      var logoAlpha = (cfg.alphaMean + cfg.alphaAmp * breathe) * fl;

      if (logoReady && logoLayer) {
        var s = 1 + 0.05 * prog;
        var cx = logoBox.x + logoBox.w / 2, cy = logoBox.y + logoBox.h / 2;
        ctx.save();
        ctx.translate(cx + jx, cy + parallax + jy);
        ctx.scale(s, s);
        ctx.translate(-cx, -cy);
        ctx.globalAlpha = logoAlpha;
        ctx.drawImage(logoLayer, 0, 0, W, H);
        if (sx !== null) drawLogoBoost(sx, logoAlpha * 0.8 * (isStatic ? 0.5 : 1));
        ctx.restore();
      }

      if (sx !== null) drawSweep(sx, sweepPeak * fl);
      drawGrain(grainIdx, cfg.grainAlpha * fl);

      if (!isStatic) {
        if (timeSec >= nextScratchAt) {
          scratchX = rand() * W;
          scratchUntil = timeSec + 0.12;
          nextScratchAt = timeSec + 5 + rand() * 4;
        }
        if (timeSec < scratchUntil) drawScratch(scratchX, cfg.scratchAlpha);
      }
    }

    function renderStatic() {
      updateScroll();
      render(0, true);
    }

    function tick(now) {
      if (self._destroyed) { self._raf = null; return; }
      if (!self._visible) { self._raf = null; return; } // el IntersectionObserver lo relanza
      try {
        if (now - lastFrame >= MIN_FRAME_MS) {
          lastFrame = now;
          updateScroll();
          render(debugTime !== null ? debugTime : (now - t0) / 1000, false);
        }
      } catch (e) {
        // Un fallo de dibujo no debe dejar el fondo congelado para toda la visita.
      } finally {
        self._raf = self._destroyed ? null : global.requestAnimationFrame(tick);
      }
    }

    // ---------------------------------------------------------------- Arranque
    this._destroyed = false;
    resize();
    updateScroll();
    // Visibilidad inicial medida aquí mismo: si se re-inicializa con la sección
    // ya en pantalla, no se depende de que el IntersectionObserver llegue a tiempo.
    var r0 = sectionEl.getBoundingClientRect();
    this._visible = r0.bottom > 0 && r0.top < global.innerHeight;

    logoImg.decoding = 'async';
    try { logoImg.crossOrigin = 'anonymous'; } catch (e) {}
    logoImg.onload = function () {
      logoReady = true;
      measureArt();
      layoutLogo();
      buildLogoLayer();
      if (reduced) renderStatic();
    };
    logoImg.src = logoSrc;

    if (reduced) {
      renderStatic();
      if (!usingSticky) {
        this._scrollHandler = updateScroll;
        global.addEventListener('scroll', this._scrollHandler, { passive: true });
      }
    } else {
      // Primer fotograma siempre (base + grano), aunque la sección esté fuera
      // de pantalla; el bucle solo sigue si está visible.
      render(0, false);
      if (this._visible) this._raf = global.requestAnimationFrame(tick);
    }

    this._resizeHandler = function () { resize(); updateScroll(); if (reduced) renderStatic(); };
    global.addEventListener('resize', this._resizeHandler);

    // La sección crece cuando cargan las fotos: sin esto, en navegadores sin
    // sticky (translateY) el fondo se quedaría corto al final de la sección.
    if ('ResizeObserver' in global) {
      var pendingRo = false;
      this._ro = new ResizeObserver(function () {
        if (pendingRo) return;
        pendingRo = true;
        global.requestAnimationFrame(function () {
          pendingRo = false;
          if (self._destroyed) return;
          var h = Math.round(sectionEl.getBoundingClientRect().height);
          if (h !== sectionHeight) { resize(); updateScroll(); if (reduced) renderStatic(); }
        });
      });
      this._ro.observe(sectionEl);
    }

    if ('IntersectionObserver' in global) {
      this._io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          self._visible = entries[i].isIntersecting;
          if (self._visible && !reduced && !self._raf && !self._destroyed) {
            self._raf = global.requestAnimationFrame(tick);
          }
        }
      }, { threshold: 0 });
      this._io.observe(sectionEl);
    }

    this._cleanupInternal = function () {
      if (this._raf) global.cancelAnimationFrame(this._raf);
      if (this._io) this._io.disconnect();
      if (this._ro) this._ro.disconnect();
      if (this._resizeHandler) global.removeEventListener('resize', this._resizeHandler);
      if (this._scrollHandler) global.removeEventListener('scroll', this._scrollHandler);
      logoImg.onload = null;
      if (this._root && this._root.parentNode) this._root.parentNode.removeChild(this._root);
      sectionEl.style.overflow = prevOverflow;
      sectionEl.style.position = prevPosition;
      this._raf = null; this._io = null; this._ro = null; this._root = null;
      this._resizeHandler = null; this._scrollHandler = null;
    };
  };

  BelmourdeStoryBg.prototype.destroy = function () {
    if (this._destroyed) return;
    if (this._cleanupInternal) this._cleanupInternal();
    this._destroyed = true;
  };

  global.BelmourdeStoryBg = new BelmourdeStoryBg();
})(window);
